<script setup>
import { ref, computed } from 'vue'
import { MenuItem } from '@headlessui/vue'
import { Icon } from '@iconify/vue'
import { positionSubDropdown } from '@/hooks/useSubDropdown'

const props = defineProps({
  icon:         { type: String,  default: null },
  trailingIcon: { type: String,  default: null },   // icon shown on the right side
  danger:       { type: Boolean, default: false },
  disabled:     { type: Boolean, default: false },
  divider:      { type: Boolean, default: false },   // separator above this item
  items:        { type: Array,   default: null },    // nested submenu (compact mode only)
  standalone:   { type: Boolean, default: false },   // render as plain button, not MenuItem
  active:       { type: Boolean, default: false },   // standalone: show focus/active bg
  size:         { type: String,  default: 'compact' }, // 'compact' | 'panel'
  as:           { type: String,  default: null },    // element override: 'a', 'div', etc.
  href:         { type: String,  default: null },    // turns row into a link
  // ── Style overrides ──────────────────────────────────────────────────────
  itemClass:    { type: String,  default: '' },      // extra classes on the row (append last; use `!` to beat defaults)
  iconClass:    { type: String,  default: '' },      // leading icon — when set, REPLACES the default `size-4 text-gray-400`
  textClass:    { type: String,  default: '' },      // label span — appended (its own color beats the row's inherited color)
})

const emit = defineEmits(['select'])

const rowBase = computed(() =>
  props.size === 'panel'
    ? 'flex items-center gap-3 w-full h-[37px] px-4 text-sm text-left transition duration-150'
    : 'flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition duration-150'
)
const rowActive   = 'bg-gray-50'
const rowHover    = computed(() => props.size === 'panel' ? 'hover:bg-gray-50' : 'hover:bg-gray-50')
const rowDisabled = 'opacity-40 cursor-not-allowed pointer-events-none'
const rowDanger   = 'text-red-500'
const rowDefault  = 'text-gray-700'

// Leading icon keeps `shrink-0`; `iconClass` (when given) owns size + color so
// callers can fully restyle it. Label keeps its structural classes and appends
// `textClass`.
const leadingIconClass = computed(() => ['shrink-0', props.iconClass || 'size-4 text-gray-400'])
const labelClass       = computed(() => ['flex-1 min-w-0 truncate', props.textClass])

// Row text color. If the caller declares a `text-*` in `itemClass`, drop the
// default (`rowDefault`/`rowDanger`) so their color wins WITHOUT needing `!` —
// two same-utility `text-*` classes otherwise collide unpredictably (stylesheet
// order, not attribute order, decides). The label span inherits this color; the
// leading icon keeps its own color unless `iconClass` is passed.
const rowTextColor = computed(() => {
  if (/(?:^|\s)!?text-/.test(props.itemClass)) return ''
  return props.danger ? rowDanger : rowDefault
})

const el = computed(() => props.as || (props.href ? 'a' : 'button'))

// Divider used to be a separate top-level `<hr>`, which made the component
// render a fragment (2 root nodes). That broke native attr/listener fallthrough
// (`@click` "extraneous listener" warning) AND HeadlessUI's `MenuItem` focus
// (`e3.focus is not a function`), since it resolved the ref to the comment node.
// Fold the separator into the row's own top border so each branch is single-root.
const dividerClass = computed(() => (props.divider ? 'mt-1 border-t border-gray-100' : ''))

// Nested submenu smart positioning
const nestedTriggerRef = ref(null)
const nestedStyle      = ref({})
const nestedPosition   = ref('right') // 'right' | 'below'

const onNestedEnter = () => {
  if (!nestedTriggerRef.value) return
  const rect = nestedTriggerRef.value.getBoundingClientRect()
  const result = positionSubDropdown(rect)
  nestedStyle.value    = result.style
  nestedPosition.value = result.position
}
</script>

<template>
  <template v-if="standalone">
    <component
      :is="el"
      :href="href || undefined"
      :type="el === 'button' ? 'button' : undefined"
      :disabled="el === 'button' ? disabled : undefined"
      :class="[
        rowBase,
        disabled ? rowDisabled : rowHover,
        active ? rowActive : '',
        rowTextColor,
        dividerClass,
        itemClass,
      ]"
      @click="emit('select')"
    >
      <slot name="icon"><Icon v-if="icon" :icon="icon" :class="leadingIconClass" /></slot>
      <span :class="labelClass"><slot /></span>
      <Icon v-if="trailingIcon" :icon="trailingIcon" class="size-4 shrink-0 text-gray-400" />
    </component>
  </template>

  <div v-else-if="items?.length" :class="['relative group/nested', dividerClass]" @mouseenter="onNestedEnter">
    <button
      ref="nestedTriggerRef"
      type="button"
      :disabled="disabled"
      :class="[rowBase, disabled ? rowDisabled : 'hover:' + rowActive, rowTextColor, itemClass]"
    >
      <slot name="icon"><Icon v-if="icon" :icon="icon" :class="leadingIconClass" /></slot>
      <span :class="labelClass"><slot /></span>
      <Icon icon="heroicons:chevron-right-16-solid" class="size-4 shrink-0 text-gray-400" />
    </button>

    <!-- Submenu panel — JS-positioned, shown on hover via CSS group -->
    <div
      :style="nestedStyle"
      class="
        fixed z-[9999]
        bg-white rounded-lg shadow-lg ring-1 ring-gray-200 py-1
        invisible group-hover/nested:visible
        opacity-0 group-hover/nested:opacity-100
        scale-95 group-hover/nested:scale-100
        transition-all duration-100
      "
    >
      <button
        v-for="sub in items"
        :key="sub.label"
        type="button"
        :disabled="sub.disabled"
        :class="[
          rowBase,
          sub.disabled ? rowDisabled : 'hover:bg-gray-50',
          sub.danger ? rowDanger : rowDefault,
        ]"
        @click="emit('select', sub)"
      >
        <Icon v-if="sub.icon" :icon="sub.icon" class="size-4 shrink-0 text-gray-400" />
        <span class="flex-1 truncate">{{ sub.label }}</span>
      </button>
    </div>
  </div>

  <MenuItem
    v-else
    v-slot="{ active, disabled: itemDisabled }"
    :disabled="disabled"
    as="template"
  >
    <button
      type="button"
      :class="[
        rowBase,
        active ? rowActive : '',
        itemDisabled ? rowDisabled : '',
        rowTextColor,
        dividerClass,
        itemClass,
      ]"
      @click="emit('select', { label: $slots.default?.()[0]?.children })"
    >
      <slot name="icon"><Icon v-if="icon" :icon="icon" :class="leadingIconClass" /></slot>
      <span :class="labelClass"><slot /></span>
      <Icon v-if="trailingIcon" :icon="trailingIcon" class="size-4 shrink-0 text-gray-400" />
    </button>
  </MenuItem>
</template>
